import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, Download, Calendar, User, Filter, Search } from "lucide-react";
import { toast } from "sonner";
import DatePickerInput from "@/components/DatePickerInput";

const ExportLogsPanel = ({ user }) => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 10;

  // Default to today only
  const todayStr = new Date().toLocaleDateString("en-CA");
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);
  // applied* keeps the values currently driving fetch; user edits start/endDate
  // and clicks "Terapkan" to commit them.
  const [appliedStart, setAppliedStart] = useState(todayStr);
  const [appliedEnd, setAppliedEnd] = useState(todayStr);

  useEffect(() => {
    fetchLogs();
  }, [currentPage, appliedStart, appliedEnd]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", currentPage);
      params.append("page_size", itemsPerPage);
      if (appliedStart) {
        const s = new Date(appliedStart + "T00:00:00");
        params.append("start_date", s.toISOString());
      }
      if (appliedEnd) {
        const e = new Date(appliedEnd + "T23:59:59");
        params.append("end_date", e.toISOString());
      }
      const response = await axiosInstance.get(`/export-logs?${params.toString()}`);
      setLogs(response.data.logs);
      setTotalCount(response.data.total);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      console.error("Error fetching export logs:", error);
      toast.error("Gagal memuat log export");
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilter = () => {
    setCurrentPage(1);
    setAppliedStart(startDate);
    setAppliedEnd(endDate);
  };

  const handleResetFilter = () => {
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
    setAppliedStart("");
    setAppliedEnd("");
  };

  const handlePresetDate = (preset) => {
    const today = new Date();
    let s = new Date();
    let e = new Date();
    switch (preset) {
      case "today":
        s = e = new Date();
        break;
      case "yesterday":
        s = e = new Date(today.getTime() - 86400000);
        break;
      case "this_week": {
        const dow = today.getDay();
        const offset = dow === 0 ? -6 : 1 - dow;
        s = new Date(today);
        s.setDate(today.getDate() + offset);
        e = new Date(today);
        break;
      }
      case "this_month":
        s = new Date(today.getFullYear(), today.getMonth(), 1);
        e = new Date(today);
        break;
      default:
        break;
    }
    const fmt = (d) => d.toLocaleDateString("en-CA");
    setStartDate(fmt(s));
    setEndDate(fmt(e));
    setCurrentPage(1);
    setAppliedStart(fmt(s));
    setAppliedEnd(fmt(e));
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString("id-ID", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatFilters = (filters) => {
    if (!filters) return "-";
    
    const filterArray = [];
    if (filters.marketing_name) filterArray.push(`Marketing: ${filters.marketing_name}`);
    if (filters.website) filterArray.push(`Website: ${filters.website}`);
    if (filters.closed_by) filterArray.push(`Follow Up: ${filters.closed_by}`);
    if (filters.start_date) {
      const startDate = new Date(filters.start_date).toLocaleDateString("id-ID");
      filterArray.push(`Dari: ${startDate}`);
    }
    if (filters.end_date) {
      const endDate = new Date(filters.end_date).toLocaleDateString("id-ID");
      filterArray.push(`Sampai: ${endDate}`);
    }
    
    return filterArray.length > 0 ? filterArray.join(", ") : "Tanpa filter";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f0f10] via-[#1a1a1b] to-[#0f0f10]">
      <header className="bg-[#1a1a1b]/80 backdrop-blur-xl border-b border-red-900/30 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate("/dashboard")}
                variant="ghost"
                size="sm"
                className="text-gray-300 hover:text-white hover:bg-red-900/20"
              >
                <ArrowLeft className="mr-2" size={18} />
                Kembali
              </Button>
              <h1 className="text-2xl font-bold text-red-50">Log Export Download</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="bg-[#1a1a1b]/80 backdrop-blur-xl border-red-900/30">
          <CardHeader>
            <CardTitle className="text-red-50">Riwayat Export Member Report</CardTitle>
            <CardDescription className="text-gray-400">
              Log semua aktivitas download/export oleh supervisor ({totalCount} total records)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Filter Tanggal */}
            <div className="mb-6 p-4 rounded-lg border border-red-900/30 bg-[#0f0f10]/50">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300 mb-2 block">Dari Tanggal</Label>
                  <DatePickerInput
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="bg-[#0f0f10] border-red-900/30 text-white"
                    data-testid="export-logs-start-date"
                  />
                </div>
                <div>
                  <Label className="text-gray-300 mb-2 block">Sampai Tanggal</Label>
                  <DatePickerInput
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="bg-[#0f0f10] border-red-900/30 text-white"
                    data-testid="export-logs-end-date"
                  />
                </div>
              </div>
              <div className="mt-4">
                <Label className="text-gray-300 mb-2 block">Preset Tanggal</Label>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => handlePresetDate("today")} variant="outline" size="sm" className="border-red-900/30 text-white hover:bg-red-900/20" data-testid="export-logs-preset-today">Hari Ini</Button>
                  <Button onClick={() => handlePresetDate("yesterday")} variant="outline" size="sm" className="border-red-900/30 text-white hover:bg-red-900/20" data-testid="export-logs-preset-yesterday">Kemarin</Button>
                  <Button onClick={() => handlePresetDate("this_week")} variant="outline" size="sm" className="border-red-900/30 text-white hover:bg-red-900/20" data-testid="export-logs-preset-week">Minggu Ini</Button>
                  <Button onClick={() => handlePresetDate("this_month")} variant="outline" size="sm" className="border-red-900/30 text-white hover:bg-red-900/20" data-testid="export-logs-preset-month">Bulan Ini</Button>
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <Button onClick={handleApplyFilter} className="bg-red-600 hover:bg-red-700 text-white" data-testid="export-logs-apply-filter">
                  <Search size={16} className="mr-2" /> Terapkan Filter
                </Button>
                <Button onClick={handleResetFilter} variant="outline" className="border-red-900/30 text-white hover:bg-red-900/20" data-testid="export-logs-reset-filter">
                  Reset
                </Button>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-8 text-gray-400">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500 mx-auto"></div>
                <p className="mt-2">Memuat data...</p>
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                Belum ada log export
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-red-900/30 hover:bg-red-900/5">
                      <TableHead className="text-red-400 font-semibold">Tanggal & Waktu</TableHead>
                      <TableHead className="text-red-400 font-semibold">User</TableHead>
                      <TableHead className="text-red-400 font-semibold">Filter</TableHead>
                      <TableHead className="text-red-400 font-semibold text-center">Jumlah Data</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log, index) => (
                      <TableRow 
                        key={log.id || index} 
                        className="border-red-900/20 hover:bg-red-900/10 transition-colors"
                      >
                        <TableCell className="text-gray-300">
                          <div className="flex items-center gap-2">
                            <Calendar size={16} className="text-gray-500" />
                            {formatDate(log.created_at)}
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-300">
                          <div className="flex items-center gap-2">
                            <User size={16} className="text-gray-500" />
                            <div>
                              <div className="font-semibold">{log.user_name}</div>
                              <div className="text-xs text-gray-500">{log.user_id}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-300">
                          <div className="flex items-center gap-2">
                            <Filter size={16} className="text-gray-500" />
                            <span className="text-sm">{formatFilters(log.filters)}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-2">
                            <Download size={16} className="text-green-500" />
                            <span className="font-semibold text-green-400">{log.record_count}</span>
                            <span className="text-gray-500 text-sm">records</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            
            {/* Pagination */}
            {!loading && totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <Button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  variant="outline"
                  size="sm"
                  className="border-red-900/30 text-white disabled:opacity-50"
                >
                  Sebelumnya
                </Button>
                <div className="flex gap-1">
                  {(() => {
                    const maxVisiblePages = 5;
                    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                    
                    if (endPage - startPage < maxVisiblePages - 1) {
                      startPage = Math.max(1, endPage - maxVisiblePages + 1);
                    }
                    
                    const pages = [];
                    
                    // First page
                    if (startPage > 1) {
                      pages.push(
                        <Button
                          key={1}
                          onClick={() => handlePageChange(1)}
                          variant="outline"
                          size="sm"
                          className="border-red-900/30 text-white"
                        >
                          1
                        </Button>
                      );
                      if (startPage > 2) {
                        pages.push(<span key="dots-start" className="text-gray-400 px-2">...</span>);
                      }
                    }
                    
                    // Visible pages
                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <Button
                          key={i}
                          onClick={() => handlePageChange(i)}
                          variant={currentPage === i ? "default" : "outline"}
                          size="sm"
                          className={currentPage === i 
                            ? "bg-red-600 text-white" 
                            : "border-red-900/30 text-white"
                          }
                        >
                          {i}
                        </Button>
                      );
                    }
                    
                    // Last page
                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pages.push(<span key="dots-end" className="text-gray-400 px-2">...</span>);
                      }
                      pages.push(
                        <Button
                          key={totalPages}
                          onClick={() => handlePageChange(totalPages)}
                          variant="outline"
                          size="sm"
                          className="border-red-900/30 text-white"
                        >
                          {totalPages}
                        </Button>
                      );
                    }
                    
                    return pages;
                  })()}
                </div>
                <Button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  variant="outline"
                  size="sm"
                  className="border-red-900/30 text-white disabled:opacity-50"
                >
                  Selanjutnya
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ExportLogsPanel;
